<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));

const ledColor = computed(() => props.block.digitColor || "#ff3b1d");
const containerStyle = computed(
  () =>
    ({
      background: props.block.backgroundColor || "#0a0a0a",
      fontFamily:
        props.block.fontFamily ||
        "'DSEG7 Classic', 'Share Tech Mono', 'Courier New', monospace",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--led-color": ledColor.value,
    }) as Record<string, string | number | undefined>,
);

const digitFontSize = computed(
  () => `${props.block.digitFontSize * layout.value.digitScale * 1.4}px`,
);
</script>

<template>
  <div class="cd-led" :style="containerStyle">
    <div class="cd-led__noise" aria-hidden="true" />
    <div class="cd-led__reflection" aria-hidden="true" />

    <template v-for="(seg, i) in segments" :key="seg.key">
      <div class="cd-led__cell">
        <div class="cd-led__panel">
          <span class="cd-led__ghost" :style="{ fontSize: digitFontSize }"
            >88</span
          >
          <span class="cd-led__digit" :style="{ fontSize: digitFontSize }">{{
            seg.value
          }}</span>
        </div>
        <div
          class="cd-led__label"
          :style="{
            fontSize: `${block.labelFontSize}px`,
            color: block.labelColor || 'rgba(255, 80, 60, 0.65)',
          }"
        >
          {{ seg.label }}
        </div>
      </div>
      <div
        v-if="i < segments.length - 1 && !layout.stack"
        class="cd-led__sep"
        :style="{ fontSize: digitFontSize }"
      >
        {{ separator }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.cd-led {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  isolation: isolate;
}
.cd-led__noise {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(255, 255, 255, 0.03) 1px,
    transparent 1px
  );
  background-size: 3px 3px;
  pointer-events: none;
  mix-blend-mode: overlay;
  z-index: 1;
}
.cd-led__reflection {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.04) 0%,
    transparent 25%,
    transparent 75%,
    rgba(255, 255, 255, 0.02) 100%
  );
  pointer-events: none;
  z-index: 2;
}
.cd-led__cell {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.cd-led__panel {
  position: relative;
  padding: 8px 14px;
  background: #060606;
  border: 2px solid #1a1a1a;
  border-radius: 6px;
  box-shadow:
    inset 0 0 15px rgba(0, 0, 0, 0.9),
    0 0 12px rgba(255, 60, 30, 0.18);
  display: inline-block;
  min-width: 2ch;
}
.cd-led__ghost {
  position: absolute;
  inset: 8px 14px;
  color: rgba(255, 60, 30, 0.07);
  letter-spacing: 0.08em;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}
.cd-led__digit {
  position: relative;
  color: var(--led-color);
  letter-spacing: 0.08em;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 6px var(--led-color),
    0 0 16px color-mix(in srgb, var(--led-color) 60%, transparent),
    0 0 32px color-mix(in srgb, var(--led-color) 30%, transparent);
  font-weight: 700;
  display: inline-block;
}
.cd-led__label {
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-family: "Helvetica Neue", sans-serif;
  font-weight: 500;
}
.cd-led__sep {
  position: relative;
  z-index: 3;
  color: var(--led-color);
  text-shadow: 0 0 8px var(--led-color);
  font-weight: 700;
  animation: cd-led-blink 1s steps(2, end) infinite;
}
@keyframes cd-led-blink {
  50% {
    opacity: 0.25;
  }
}
</style>
